import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { AuthService } from '../../services/auth/auth.service';
import { SearchService } from '../../services/search/search.service';
import { ReturnUrlService } from '../../services/return-url/return-url.service';
import { SitesService } from '../../services/sites/sites.service';
import { FlashMessagesService } from '../../services/flash-messages/flash-messages.service';
import { Site } from '../../services/sites/site.model';
import { WindowService } from '../../services/window/window.service';

@Component({
    selector: 'app-gps-search',
    templateUrl: './gps-search.component.html',
    styleUrls: ['./gps-search.component.css']
})
export class GpsSearchComponent implements OnDestroy {
    messageIds = {
        httpError: <string>null
    };

    savedLatitude: string;
    savedLongitude: string;
    private routeSub: Subscription;
    private querySub: Subscription;
    private userSub: Subscription;

    constructor(
        private activatedRoute: ActivatedRoute,
        public authService: AuthService,
        private router: Router,
        private returnUrlService: ReturnUrlService,
        public searchService: SearchService,
        private sitesService: SitesService,
        private flashMessagesService: FlashMessagesService,
        private windowService: WindowService) {

        this.searchService.latitude = Number(this.windowService.localStorage.getItem('latitudeSearchValue'));
        this.searchService.longitude = Number(this.windowService.localStorage.getItem('longitudeSearchValue'));

        this.userSub = this.authService.userInfoSubject.pipe(
            filter(user => !!user),
            take(1)
        ).subscribe(() => {
            console.log('GpsSearchComponent: User info received, processing route parameters...');
            this.processRouteParameters();
        });

        this.returnUrlService.save();
    }

    // --- Public Getters ---
    get canSearch(): boolean {
        return !this.selectSiteEnabled || this.searchService.site != null;
    }

    get selectSiteEnabled(): boolean {
        // Ensure userInfo exists before accessing its properties
        return !!this.authService.userInfo && this.authService.userInfo.isInRole('jjr');
    }

    // --- Lifecycle Hooks ---
    ngOnDestroy(): void {
        this.searchService.clearAll();
        // Unsubscribe to prevent memory leaks
        if (this.userSub) { this.userSub.unsubscribe(); }
        if (this.routeSub) { this.routeSub.unsubscribe(); }
        if (this.querySub) { this.querySub.unsubscribe(); }
    }

    // --- Public Methods ---
    search(): void {
        this.windowService.localStorage.setItem('latitudeSearchValue', this.searchService.latitude.toString());
        this.windowService.localStorage.setItem('longitudeSearchValue', this.searchService.longitude.toString());

        const commands = ['gps-search'];
        if (this.searchService.site) {
            commands.push(this.searchService.site.siteNumber);
        }

        const extras: NavigationExtras = { queryParams: {} };
        if (this.searchService.latitude) {
            extras.queryParams.latitude = this.searchService.latitude;
        }

        if (this.searchService.longitude) {
            extras.queryParams.longitude = this.searchService.longitude;
        }

        this.savedLatitude = this.windowService.localStorage.getItem('latitudeSearchValue');
        this.savedLongitude = this.windowService.localStorage.getItem('longitudeSearchValue');

        this.router.navigate(commands, extras).then((success: boolean) => {
            this.searchService.search(null, Number(this.savedLatitude), Number(this.savedLongitude));
            this.returnUrlService.save();
        });
    }

    selectSite(site: Site): void {
        this.searchService.selectSite(site);
        this.searchService.latitude = null;
        this.searchService.longitude = null;
    }

    // --- Private Methods ---
    // Moved route processing logic into a separate method
    private processRouteParameters(): void {
        // TODO: Replace explicit calls to search() and searchService.search() with combination of observables.
        // Routing to the same component doesn't recreate the component so we must subscribe to queryParams changes.
        this.routeSub = this.activatedRoute.params.subscribe(params => {
            const siteParam = params['site'];
            if (siteParam) {
                this.sitesService.get(siteParam).subscribe(result => {
                    this.searchService.site = result;
                    this.searchService.search(null, this.searchService.latitude, this.searchService.longitude);
                }, error => {
                    this.messageIds.httpError = this.flashMessagesService.addMessageFor(error);
                });
            } else {
                this.searchService.search(null, this.searchService.latitude, this.searchService.longitude);
            }
        });

        this.querySub = this.activatedRoute.queryParams.subscribe(queryParams => {
            const latNew = parseFloat(queryParams['latitude']);
            const lonNew = parseFloat(queryParams['longitude']);

            // Only search if params actually change *and* are valid numbers
            if ((latNew !== this.searchService.latitude) ||
                (lonNew !== this.searchService.longitude)) {

                console.log('GpsSearchComponent: Query params changed, triggering search.');
                this.searchService.latitude = latNew;
                this.searchService.longitude = lonNew;
                this.searchService.search(null, this.searchService.latitude, this.searchService.longitude);
            }
        });

        // Initial snapshot check can likely be removed as subscriptions handle it,
        // but keep if direct navigation with all params needs immediate search.
        // const site = this.activatedRoute.snapshot.paramMap.get('site');
        // const latitude = this.activatedRoute.snapshot.queryParamMap.get('latitude');
        // const longitude = this.activatedRoute.snapshot.queryParamMap.get('longitude');
        // if (site && latitude && longitude) { ... search ... }
    }
}
